import { useTranslations } from 'next-intl'
import { useCallback, useMemo } from 'react'
import { useId } from 'react'

import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'

import { useWebPushNotificationSubscription } from '@/hooks/use-web-push-notification-subscription'

import { WebPushNotificationSubscriptionList } from './web-push-notification-subscription-list'

const ManageWebPushNotificationSubscriptionSection = () => {
  const tComponent = useTranslations('Notification.components.manage_web_push_notification_subscription_section')

  const {
    subscribeToWebPushNotification,
    unsubscribeToWebPushNotification,
    hasSubscription,
    isChangingSubscription,
    isCheckingIfSupported,
    isSupported,
    isPermissionDenied,
  } = useWebPushNotificationSubscription({ allowToast: true })

  const switchId = useId()

  const switchLabel = useMemo(() => {
    return hasSubscription ? tComponent('subscription_state.enabled') : tComponent('subscription_state.disabled')
  }, [hasSubscription, tComponent])

  const onSubscriptionToggle = useCallback(() => {
    if (isChangingSubscription) return
    if (hasSubscription) {
      unsubscribeToWebPushNotification()
    } else {
      subscribeToWebPushNotification()
    }
  }, [isChangingSubscription, hasSubscription, subscribeToWebPushNotification, unsubscribeToWebPushNotification])

  return (
    <div className="flex flex-[1_1_0] flex-col py-6">
      <div className="mb-6 flex flex-col justify-between gap-2 px-6 md:flex-row md:items-center">
        <h3 className="font-semibold tracking-tight">{tComponent('title')}</h3>
        {isCheckingIfSupported ? (
          <Skeleton className="h-10 w-20 animate-fade-in md:w-40" />
        ) : (
          isSupported &&
          !isPermissionDenied && (
            <div className="flex animate-fade-in items-center gap-2">
              <label htmlFor={switchId} className="shrink-0 cursor-pointer text-sm font-medium text-foreground">
                {switchLabel}
              </label>
              <Switch
                id={switchId}
                checked={hasSubscription}
                onCheckedChange={onSubscriptionToggle}
                disabled={isChangingSubscription}
              />
            </div>
          )
        )}
      </div>

      {!isCheckingIfSupported && !isSupported ? (
        <div className="mx-6 mb-6 rounded-lg bg-base-accent p-4">
          <h4 className="mb-1 text-base font-medium text-foreground">{tComponent('messages.not_supported.title')}</h4>
          <p className="text-sm font-normal text-muted-foreground">
            {tComponent('messages.not_supported.description')}
          </p>
        </div>
      ) : !isCheckingIfSupported && isPermissionDenied ? (
        <div className="mx-6 mb-6 rounded-lg bg-base-accent p-4">
          <h4 className="mb-1 text-base font-medium text-foreground">
            {tComponent('messages.permission_denied.title')}
          </h4>
          <p className="text-sm font-normal text-muted-foreground">
            {tComponent('messages.permission_denied.description')}
          </p>
        </div>
      ) : (
        <></>
      )}

      <WebPushNotificationSubscriptionList className="px-6" />
    </div>
  )
}
ManageWebPushNotificationSubscriptionSection.displayName = 'ManageWebPushNotificationSubscriptionSection'

export { ManageWebPushNotificationSubscriptionSection }
